<script setup lang="ts">
import { Icon } from '@iconify/vue'
import '@onenetwork/components/image_picker/image_picker.scss'
import { computed, ref, watch } from 'vue'
import defaultImage from '@/assets/default-image.svg'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { UPLOAD_API } from '@/onenetwork/constants/onenetwork.api.constant.ts'
import CsImagePreview from '@/onenetwork/components/csimage/CsImagePreview.vue'

const props = defineProps({
  width: {
    type: String,
    default: '100%',
  },
  height: {
    type: String,
    default: '100%',
  },
  uuid: {
    type: String,
    default: 'image-picker',
  },
  src: String,
  previewSrc: String,
  upload: {
    type: Boolean,
    default: false,
  },
  folder: {
    type: String,
    default: 'common',
  },
  type: {
    type: String,
    default: 'photo',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  previewIcon: {
    type: String,
    default: 'material-symbols:image-search',
  },
  hideUploadPreview: {
    type: Boolean,
    default: false,
  },
  hideRemove: {
    type: Boolean,
    default: false,
  },
  draggable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'action:open-fileman',
  'action:remove-image',
  'action:choose-image',
  'action:upload-success',
  'action:drag-start',
  'action:drag-over',
  'action:drop',
  'action:drag-end',
])

const imgsrc = ref('')
const imgpreviewSrc = ref('')
const loading = ref(false)
const path = ref('')
const isDragging = ref(false)
const isDragOver = ref(false)

const readonly = computed(() => props.readonly)

const handleOpenFileman = () => {
  emit('action:open-fileman', props.uuid)
}

const handleRemoveImage = () => {
  imgsrc.value = defaultImage
  imgpreviewSrc.value = defaultImage
  path.value = ''
  emit('action:remove-image', { uuid: props.uuid, path: path.value })
}

const fileInput = ref<HTMLInputElement | null>(null)

const handleChooseFile = () => {
  if (props.readonly) return
  fileInput.value?.click()
}

const handleChangeFile = (e: Event) => {
  const target = e.target as HTMLInputElement
  const file = target?.files?.[0]

  if (file) {
    emit('action:choose-image', { uuid: props.uuid, file })
    if (props.upload) handleUploadProgress(file)
    target.value = ''
  }
}

const handleUploadProgress = async (file: File) => {
  try {
    loading.value = true

    const formData = new FormData()
    formData.append('file', file)
    formData.append('uuid', props.uuid)
    formData.append('folder', props.folder)
    formData.append('type', props.type)

    const res = await APIClient.post(UPLOAD_API, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    if (!props.hideUploadPreview) {
      imgsrc.value = res.data.data?.url?.small || res.data.data?.url?.resize || defaultImage
      imgpreviewSrc.value = res.data.data?.url?.small || res.data.data?.url?.resize || defaultImage
      path.value = res.data.data?.path || ''
    }

    emit('action:upload-success', {
      uuid: props.uuid,
      path: path.value,
      response: res.data,
    })
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const dragPayload = () => ({
  uuid: props.uuid,
  path: path.value,
  src: imgsrc.value,
  previewSrc: imgpreviewSrc.value,
})

const handleDragStart = (e: DragEvent) => {
  if (!props.draggable) return

  isDragging.value = true

  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('application/json', JSON.stringify(dragPayload()))
  }

  emit('action:drag-start', dragPayload())
}

const handleDragEnter = (e: DragEvent) => {
  if (!props.draggable) return
  e.preventDefault()
  isDragOver.value = true
}

const handleDragOver = (e: DragEvent) => {
  if (!props.draggable) return

  e.preventDefault()

  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move'
  }

  isDragOver.value = true
  emit('action:drag-over', dragPayload())
}

const handleDragLeave = () => {
  if (!props.draggable) return
  isDragOver.value = false
}

const handleDrop = (e: DragEvent) => {
  if (!props.draggable) return

  e.preventDefault()
  isDragOver.value = false

  let from = null

  try {
    const raw = e.dataTransfer?.getData('application/json')
    if (raw) from = JSON.parse(raw)
  } catch (error) {
    console.log(error)
  }

  emit('action:drop', {
    from,
    to: dragPayload(),
  })
}

const handleDragEnd = () => {
  if (!props.draggable) return
  isDragging.value = false
  isDragOver.value = false
  emit('action:drag-end', dragPayload())
}

watch(
  () => props.src,
  () => {
    imgsrc.value = props.src || defaultImage
  },
  { immediate: true },
)

watch(
  () => props.previewSrc,
  () => {
    imgpreviewSrc.value = props.previewSrc || props.src || defaultImage
  },
  { immediate: true },
)
</script>

<template>
  <div
    class="image-picker"
    :class="{
      'is-draggable': props.draggable,
      'is-dragging': isDragging,
      'is-drag-over': isDragOver,
    }"
    :style="{ width: props.width, height: props.height }"
    :draggable="props.draggable"
    @dragstart="handleDragStart"
    @dragenter="handleDragEnter"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
    @dragend="handleDragEnd"
  >
    <template v-if="!readonly">
      <div class="image-preview" @click="handleChooseFile">
        <cs-image-preview :id="`ipk-preview-${props.uuid}`" :src="imgsrc" alt="Image" />
        <div class="preview-icon">
          <Icon :icon="props.previewIcon" />
        </div>
      </div>
    </template>

    <template v-else>
      <cs-image-preview
        :id="`ipk-preview-${props.uuid}`"
        :src="imgsrc"
        :previewSrc="imgpreviewSrc || imgsrc"
        alt="Image"
        :preview="true"
        :type="props.type"
      />
    </template>

    <div class="image-caption" v-if="!readonly">
      <div>
        <span :id="`ipk-caption-${props.uuid}`">
          {{ loading ? 'Đang tải...' : 'Chọn file' }}
        </span>

        <span v-if="props.draggable" class="drag-hint">
          <Icon icon="mdi:drag" /> Kéo để đổi vị trí
        </span>

        <span class="remove" @click="handleRemoveImage" v-if="!props.hideRemove">
          <Icon icon="iconamoon:trash-fill" /> Gỡ
        </span>
      </div>
    </div>
    <input
      style="display: none"
      ref="fileInput"
      type="file"
      @change="handleChangeFile"
      accept="image/*"
    />
  </div>
</template>

<style scoped>
.image-picker.is-draggable {
  cursor: move;
}

.image-picker.is-dragging {
  opacity: 0.6;
}

.image-picker.is-drag-over {
  outline: 2px dashed #3b82f6;
  outline-offset: 4px;
  border-radius: 8px;
}

.drag-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  font-size: 12px;
  color: #666;
}
</style>
